import { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../services/api';

function ForgotPassword() {
  const [identifier, setIdentifier] = useState('');
  const [step, setStep]             = useState('input');   // 'input' | 'confirm' | 'sent'
  const [userData, setUserData]     = useState(null);      // { full_name, masked_email }
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [focused, setFocused]       = useState(false);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { setError('Please enter your Employee Code or Username.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await auth.lookupResetUser(identifier.trim());
      setUserData(res.data);
      setStep('confirm');
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'No active employee found with that code or username.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setError('');
    setLoading(true);
    try {
      await auth.forgotPassword(identifier.trim());
      setStep('sent');
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (active) => ({
    width: '100%', boxSizing: 'border-box',
    padding: '0.72rem 1rem',
    border: `1.5px solid ${active ? '#F97316' : '#E5E7EB'}`,
    borderRadius: '0.5rem', fontSize: '0.9rem', color: '#111827',
    background: '#FFFFFF', outline: 'none', transition: 'border-color 0.15s',
    boxShadow: active ? '0 0 0 3px rgba(249,115,22,0.1)' : 'none',
  });

  const btnStyle = (disabled) => ({
    width: '100%', padding: '0.8rem',
    background: disabled ? '#FDBA74' : '#F97316',
    color: '#FFFFFF', border: 'none', borderRadius: '0.5rem',
    fontSize: '0.9rem', fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    transition: 'background 0.15s',
  });

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F9FAFB', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: '1.5rem',
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{
        width: '100%', maxWidth: '420px',
        background: '#FFFFFF', borderRadius: '12px',
        boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}>
        <div style={{ height: '3px', background: '#F97316' }} />

        <div style={{ padding: '2.5rem' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img
              src="/btlogo.png" alt="BonTon"
              style={{ height: '32px', objectFit: 'contain', marginBottom: '1rem' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', margin: '0 0 0.3rem' }}>
              Forgot Password
            </h1>
            <p style={{ color: '#6B7280', fontSize: '0.875rem', margin: 0 }}>
              {step === 'input'   && 'Enter your Employee Code or Username.'}
              {step === 'confirm' && 'Confirm your official email to receive the reset link.'}
              {step === 'sent'    && 'Reset link sent to your official email.'}
            </p>
          </div>

          {/* ── Step: sent ── */}
          {step === 'sent' && (
            <div style={{
              background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '0.5rem',
              padding: '1.25rem', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📧</div>
              <p style={{ color: '#15803D', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
                Check your inbox
              </p>
              <p style={{ color: '#166534', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>
                A password reset link has been sent to your official email address. It expires in 15 minutes.
              </p>
              <Link to="/login" style={{
                display: 'inline-block', marginTop: '1.25rem',
                color: '#F97316', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none',
              }}>
                ← Back to Sign In
              </Link>
            </div>
          )}

          {/* ── Step: input ── */}
          {step === 'input' && (
            <>
              {error && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem',
                  padding: '0.75rem 1rem', marginBottom: '1.25rem',
                  color: '#DC2626', fontSize: '0.82rem',
                }}>
                  {error}
                </div>
              )}
              <form onSubmit={handleLookup}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#374151', marginBottom: '0.4rem' }}>
                    Employee Code or Username
                  </label>
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="e.g. BTC1001"
                    autoFocus
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    style={inputStyle(focused)}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={btnStyle(loading)}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#EA580C'; }}
                  onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#F97316'; }}
                >
                  {loading ? (
                    <><span style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 0.75s linear infinite', display: 'inline-block', flexShrink: 0 }} />Looking up...</>
                  ) : 'Continue'}
                </button>
              </form>
              <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.85rem' }}>
                <Link to="/login" style={{ color: '#F97316', fontWeight: 600, textDecoration: 'none' }}>← Back to Sign In</Link>
              </p>
            </>
          )}

          {/* ── Step: confirm ── */}
          {step === 'confirm' && userData && (
            <>
              {error && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem',
                  padding: '0.75rem 1rem', marginBottom: '1.25rem',
                  color: '#DC2626', fontSize: '0.82rem',
                }}>
                  {error}
                </div>
              )}

              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '0.5rem', padding: '1.25rem', marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: '#6B7280' }}>Employee</p>
                <p style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: '#111827' }}>{userData.full_name}</p>
                <p style={{ margin: '0 0 0.4rem', fontSize: '0.82rem', color: '#6B7280' }}>Reset link will be sent to</p>
                <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#F97316', letterSpacing: '0.02em' }}>{userData.masked_email}</p>
              </div>

              <button
                onClick={handleSend}
                disabled={loading}
                style={btnStyle(loading)}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#EA580C'; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#F97316'; }}
              >
                {loading ? (
                  <><span style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 0.75s linear infinite', display: 'inline-block', flexShrink: 0 }} />Sending...</>
                ) : 'Send Reset Link'}
              </button>

              <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.85rem' }}>
                <button onClick={() => { setStep('input'); setError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F97316', fontWeight: 600, fontSize: '0.85rem' }}>
                  ← Use a different code
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
